import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { Scene, ScenePart } from './scenes';

export type Placed = { emoji?: string; text?: string; size?: number };

export function SceneView({ scene, placed = {}, overlay, onPart, outline, hits }: {
  scene: Scene;
  placed?: Record<string, Placed>;
  overlay?: (part: ScenePart, left: string, top: string) => ReactNode;
  onPart?: (id: string) => void;
  outline?: string | null;
  hits?: readonly string[];
}) {
  const pct = (x: number, total: number) => `${(x / total) * 100}%`;
  const behind = scene.parts.filter((p) => p.behind && placed[p.id]);
  const inside = scene.parts.filter((p) => p.inside && placed[p.id]);
  const above = scene.parts.filter((p) => !p.behind && !p.inside && placed[p.id]);
  const glyph = (p: ScenePart) => {
    const v = placed[p.id];
    if (v && p.worn) return <g key={p.id} style={{ pointerEvents: 'none' }}>{p.worn}</g>;
    if (!v?.emoji) return null;
    const size = v.size ?? p.wearSize ?? 56;
    return (
      <text key={p.id} x={p.at[0]} y={p.at[1]} fontSize={size} textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: 'none' }}>
        {v.emoji}
      </text>
    );
  };
  return (
    <div className="g-scene" style={{ aspectRatio: `${scene.w} / ${scene.h}`, '--ar': scene.w / scene.h } as React.CSSProperties}>
      <svg viewBox={`0 0 ${scene.w} ${scene.h}`} role="img" aria-label={scene.label}>
        {scene.base}
        {scene.parts.map((p) => p.art && <g key={p.id} data-part={p.id}>{p.art}</g>)}
        {behind.map(glyph)}
        {scene.mid}
        {inside.map(glyph)}
        {scene.front}
        {above.map(glyph)}
        {outline && (() => {
          const p = scene.parts.find((q) => q.id === outline);
          if (!p) return null;
          const [x, y, w, h] = p.hit ?? p.box;
          return <rect className="g-outline" x={x - 4} y={y - 4} width={w + 8} height={h + 8} rx="10" />;
        })()}
        {onPart && scene.parts.filter((p) => !hits || hits.includes(p.id)).map((p) => {
          const [x, y, w, h] = p.hit ?? p.box;
          const pad = 6;
          return (
            <rect
              key={`hit-${p.id}`}
              className="g-hit"
              x={x - pad}
              y={y - pad}
              width={w + pad * 2}
              height={h + pad * 2}
              rx="8"
              role="button"
              tabIndex={0}
              aria-label={p.label}
              onClick={() => onPart(p.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPart(p.id); } }}
            />
          );
        })}
      </svg>
      {overlay && (
        <div className="g-scene-overlay">
          {scene.parts.map((p) => overlay(p, pct(p.at[0], scene.w), pct(p.at[1], scene.h)))}
        </div>
      )}
    </div>
  );
}

export function PartMini({ scene, part }: { scene: Scene; part: ScenePart }) {
  if (part.worn) return <WornMini worn={part.mini ?? part.worn} />;
  if (part.wear) return <span className="g-chip-pic g-chip-emoji" aria-hidden="true">{part.wear}</span>;
  const [x, y, w, h] = part.box;
  const side = Math.max(w, h) * 1.1;
  return (
    <svg className="g-chip-pic" viewBox={`${x + w / 2 - side / 2} ${y + h / 2 - side / 2} ${side} ${side}`} aria-hidden="true">
      {scene.base}
      {scene.parts.map((p) => p.art && <g key={p.id}>{p.art}</g>)}
    </svg>
  );
}

function WornMini({ worn }: { worn: ReactNode }) {
  const ref = useRef<SVGGElement | null>(null);
  const [box, setBox] = useState<string | null>(null);
  useLayoutEffect(() => {
    const b = ref.current?.getBBox();
    if (!b || !b.width) return;
    const side = Math.max(b.width, b.height) + 10;
    setBox(`${b.x + b.width / 2 - side / 2} ${b.y + b.height / 2 - side / 2} ${side} ${side}`);
  }, []);
  return (
    <svg className="g-chip-pic" viewBox={box ?? "0 0 400 320"} aria-hidden="true" style={box ? undefined : { visibility: "hidden" }}>
      <g ref={ref}>{worn}</g>
    </svg>
  );
}
